/**
 * 审核校验（仅单条操作栏）：执行原动作前，先按业务单据 id 查开页入口。
 * - canDeal 且 needEdit → 新开与待办相同的流程处理页
 * - canDeal / needEdit 为 false、接口失败或回包非约定格式 → continue 原动作
 * 单据编号等链接列不分流，直接走列原动作。批量审核仍走工具栏原动作。
 */
import GoingUtils from '@/utils/goingUtils';
import {
  getFlowEditCheckConfig,
  isFlowAuditNodeBtn,
  isFlowEditCheckEnabledOnTable,
  type FlowEditCheckTableConfig,
} from '@/main-page/simpleFlow/hooks/flowEditCheckRegistry';

/** 优先用表格 props，否则用注册表回查 */
function pickTableConfig(tableProps: any, gridKey?: string): FlowEditCheckTableConfig | null {
  if (tableProps && isFlowEditCheckEnabledOnTable(tableProps)) {
    return tableProps;
  }
  if (gridKey) {
    return getFlowEditCheckConfig(gridKey);
  }
  return null;
}

/** 从当前行按单据ID字段取值；未配置（空或仅空白）则读 id */
function resolveFlowBusId(cfg: FlowEditCheckTableConfig, record: any) {
  const field = String(cfg.flowBusIdField || '').trim();
  const key = field || 'id';
  return record?.[key];
}

/**
 * 按业务单据 id 打开流程处理页；openEditor 表示已开页、不再跑原 jsContent
 */
async function openSimplFlowDealFromList(
  cfg: FlowEditCheckTableConfig,
  record: any,
  viewModel: any,
  titleFallback?: string,
): Promise<'openEditor' | 'continue' | 'abort'> {
  const id = resolveFlowBusId(cfg, record);
  if (id == null || id === '') {
    GoingUtils.showWarningMsg('当前行缺少单据ID');
    return 'abort';
  }
  if (!viewModel || typeof viewModel.openSimplFlowDealByBusId !== 'function') {
    GoingUtils.showErrorMsg('无法打开流程处理页');
    return 'abort';
  }

  const result = await viewModel.openSimplFlowDealByBusId(id, {
    flowTitle: titleFallback || '审核',
  });
  if (result === 'opened') {
    return 'openEditor';
  }
  if (result === 'skipNeedEdit') {
    return 'continue';
  }
  return 'abort';
}

/**
 * 单据流程动作组：是否分流只看动作组「审核校验」，不读表格上的同名开关。
 * 按详情单据 id 查开页入口；需要编辑则打开流程处理页，否则继续打 audit。
 */
export async function runFlowActionWritableCheck(
  recordId: any,
  viewModel: any,
): Promise<'openEditor' | 'continue' | 'abort'> {
  if (recordId == null || recordId === '') {
    GoingUtils.showWarningMsg('当前单据缺少ID');
    return 'abort';
  }
  return openSimplFlowDealFromList(
    {flowBusIdField: 'id'},
    {id: recordId},
    viewModel,
    '审核',
  );
}

/**
 * 单条操作栏：按钮 isFlowAuditNode + 表格 checkFlowEditStatus → 检查后开处理页或继续原动作
 */
export async function runSingleFlowEditCheck(
  btnItem: any,
  record: any,
  viewModel: any,
  tableProps?: any,
  gridKey?: string,
): Promise<'openEditor' | 'continue' | 'abort'> {
  if (!isFlowAuditNodeBtn(btnItem)) {
    return 'continue';
  }
  const cfg = pickTableConfig(tableProps, gridKey || tableProps?.key);
  if (!cfg || !isFlowEditCheckEnabledOnTable(cfg)) {
    return 'continue';
  }

  return openSimplFlowDealFromList(
    cfg,
    record,
    viewModel,
    btnItem?.label || '审核',
  );
}
